import { Injectable } from '@angular/core';

import { BehaviorSubject, Observable, distinctUntilChanged, map } from 'rxjs';

export type ThemeMode = 'light' | 'dark' | 'system';

const STORAGE_KEY = 'theme';
const MODES: ThemeMode[] = ['light', 'dark', 'system'];

// Light, dark or following the system. theme.scss emits every color with light-dark(),
// so switching is only a matter of setting `color-scheme` on <html>.
@Injectable({
  providedIn: 'root'
})
export class ThemeService {

  private readonly systemDark = window.matchMedia?.('(prefers-color-scheme: dark)');
  private readonly _mode = new BehaviorSubject<ThemeMode>(readMode());
  // true when the dark palette is on screen, whatever the mode
  private readonly _dark = new BehaviorSubject<boolean>(false);

  constructor() {
    this.apply();
    this.systemDark?.addEventListener('change', () => this.apply());
  }

  get mode(): ThemeMode {
    return this._mode.value;
  }

  mode$(): Observable<ThemeMode> {
    return this._mode.asObservable();
  }

  // Emits after <html> already has the new color-scheme, so colors read from CSS are up to date
  dark$(): Observable<boolean> {
    return this._dark.pipe(distinctUntilChanged());
  }

  setMode(mode: ThemeMode): void {
    try {
      localStorage.setItem(STORAGE_KEY, mode);
    } catch { }
    this._mode.next(mode);
    this.apply();
  }

  // Header button: light -> dark -> system -> light
  cycleMode(): void {
    this.setMode(MODES[(MODES.indexOf(this.mode) + 1) % MODES.length]);
  }

  // Theme saved in the user settings, applied at sign-in
  themeModeChange(s: string): void {
    this.setMode(MODES.includes(s as ThemeMode) ? s as ThemeMode : 'light');
  }

  themeToogle(): void {
    this.cycleMode();
  }

  // true for the light palette (kept for the screens that already use it)
  themeStatus(): Observable<boolean> {
    return this.dark$().pipe(map(dark => !dark));
  }

  private apply(): void {
    const mode = this.mode;
    const root = document.documentElement;
    root.style.colorScheme = mode == 'system' ? 'light dark' : mode;
    root.dataset['theme'] = mode;
    this._dark.next(mode == 'dark' || (mode == 'system' && !!this.systemDark?.matches));
  }
}

function readMode(): ThemeMode {
  try {
    const saved = localStorage.getItem(STORAGE_KEY) as ThemeMode | null;
    return saved && MODES.includes(saved) ? saved : 'system';
  } catch {
    return 'system';
  }
}
